import React from 'react';
import { useI18n } from '../../../contexts/I18nContext';
import { UserDetailState } from './useUserDetail';

const roleBadgeClass = (name: string): string =>
  name === 'ClientAdmin' ? 'is-clientadmin' : name === 'Viewer' ? 'is-standard' : 'is-anep';

/** The Roles tab: the roles held on this environment, and the way to add one. */
export const UserRolesTab: React.FC<{ state: UserDetailState }> = ({ state }) => {
  const { t } = useI18n();
  const { clientRoles, isLoadingRoles, canWriteUsers, availableRoles, roleDescription } = state;

  return (
    <div className="tab-content ud-tab">
      <section className="cu-section">
        <header className="cu-section-head">
          <h3>{t('users.currentRoles')}</h3>
          <p>{t('users.resourceAccessHint')}</p>
          <div className="cu-section-actions">
            {/* The environment is not a choice: it is whichever one this frontend serves. */}
            <span className="ud-env" data-testid="resource-access-env" title={t('users.resourceAccess')}>
              <span className="resource-access-value">{state.environmentClientId}</span>
            </span>
          </div>
        </header>
        {isLoadingRoles ? (
          <p className="ud-empty">{t('users.loadingRoles')}</p>
        ) : clientRoles.length > 0 ? (
          <ul className="ud-list">
            {clientRoles.map((role) => {
              const canRemove = state.canManageRole(role.name);
              const description = roleDescription(role.name, role.description);
              return (
                <li key={role.id} className="ud-row">
                  <div className="ud-row-text">
                    <strong><span className={`role-badge ${roleBadgeClass(role.name)}`}>{role.name}</span></strong>
                    {description && <span className="ud-row-sub">{description}</span>}
                  </div>
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => state.removeRole(role.name)} disabled={isLoadingRoles || !canRemove}
                          title={!canRemove ? t('users.cantRemoveHigherRole') : undefined}>
                    {t('users.remove')}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="ud-empty">{t('users.noRolesForClient')}</p>
        )}
      </section>

      {canWriteUsers && (
        <section className="cu-section">
          <header className="cu-section-head">
            <h3>{t('users.addRole')}</h3>
            <p>{t('users.detail.addRoleHint')}</p>
          </header>
          <div className="cu-input-row ud-add-row">
            <select className="cu-input" value={state.selectedRoleToAdd} data-testid="field-user-role-name"
                    onChange={(e) => state.setSelectedRoleToAdd(e.target.value)} disabled={isLoadingRoles} aria-label={t('users.role')}>
              <option value="">{t('users.selectRole')}</option>
              {availableRoles.map((role) => <option key={role} value={role}>{role}</option>)}
            </select>
            <button type="button" className="btn btn-primary btn-sm" data-testid="btn-user-add-role" onClick={state.addRole} disabled={isLoadingRoles || !state.selectedRoleToAdd}>
              {isLoadingRoles ? t('users.adding') : t('users.addRole')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
};
